'use client';

import { UnifiedTooltip } from '@/components/ui/feedback/notification/unified-tooltip';
import { cn } from '@/lib/ui/utils';
import { type ButtonHTMLAttributes, type ReactNode, useId } from 'react';

const PREMIUM_GRADIENT_STOPS = [
  { offset: '0%', color: 'rgb(255 176 64)' },
  { offset: '24%', color: 'rgb(255 215 87)' },
  { offset: '56%', color: 'rgb(255 109 61)' },
  { offset: '78%', color: 'rgb(240 84 123)' },
  { offset: '100%', color: 'rgb(90 118 255)' },
] as const;

export const PREMIUM_GRADIENT_BACKGROUND = `linear-gradient(135deg, ${PREMIUM_GRADIENT_STOPS.map(
  (stop) => `${stop.color} ${stop.offset}`
).join(', ')})`;

interface PremiumCrownIconProps {
  appearance?: 'gradient' | 'outline';
  className?: string;
  iconClassName?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  opticalOffsetY?: string;
  showNativeTitle?: boolean;
  showTooltip?: boolean;
  title?: string | null;
  tooltipText?: string | null;
}

export function PremiumCrownIcon({
  appearance = 'gradient',
  className,
  iconClassName,
  onClick,
  opticalOffsetY = '0.16em',
  showTooltip = true,
  showNativeTitle = true,
  title = null,
  tooltipText = null,
}: PremiumCrownIconProps) {
  const gradientId = useId().replace(/:/g, '');
  const isOutline = appearance === 'outline';
  const resolvedTitle = title ?? 'Premium';
  const resolvedTooltipText =
    tooltipText ?? 'This is a premium feature. Please upgrade to access it.';
  const rootClassName = cn(
    'inline-flex items-center justify-center leading-none',
    className
  );
  const rootTitle = showTooltip || !showNativeTitle ? undefined : resolvedTitle;
  const iconContent = (
    <span className="inline-flex items-center justify-center">
      <span
        className="inline-flex items-center justify-center"
        style={{ transform: `translateY(${opticalOffsetY})` }}
      >
        <svg
          aria-hidden="true"
          className={cn(
            'block h-[1.125rem] w-[1.125rem] shrink-0 overflow-visible',
            iconClassName
          )}
          focusable="false"
          viewBox="0 0 24 24"
        >
          {!isOutline ? (
            <defs>
              <linearGradient
                id={gradientId}
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                {PREMIUM_GRADIENT_STOPS.map((stop) => (
                  <stop
                    key={stop.offset}
                    offset={stop.offset}
                    stopColor={stop.color}
                  />
                ))}
              </linearGradient>
            </defs>
          ) : null}
          <path
            d="M3 7.5 7.5 11 12 4l4.5 7L21 7.5 19 19H5L3 7.5Z"
            fill={isOutline ? 'none' : `url(#${gradientId})`}
            stroke={isOutline ? 'currentColor' : undefined}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={isOutline ? 1.8 : undefined}
          />
        </svg>
      </span>
    </span>
  );
  const icon = onClick ? (
    <button
      type="button"
      className={rootClassName}
      aria-label={resolvedTitle}
      title={rootTitle}
      onClick={onClick}
    >
      {iconContent}
    </button>
  ) : (
    <span
      className={rootClassName}
      aria-label={resolvedTitle}
      title={rootTitle}
    >
      {iconContent}
    </span>
  );

  if (!showTooltip) {
    return icon;
  }

  return (
    <UnifiedTooltip
      triggerAsChild
      trigger={icon}
      text={resolvedTooltipText}
      bodyClassName="max-w-72 text-sm leading-6 sm:max-w-80"
      contentClassName="px-3 py-2.5"
    />
  );
}

export function PremiumGradientText({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn('inline-block', className)}
      style={{
        backgroundClip: 'text',
        backgroundImage: PREMIUM_GRADIENT_BACKGROUND,
        color: 'transparent',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}
    >
      {children}
    </span>
  );
}
